import type { PublishedTmsEventStream, TmsEventStream } from "./base";
import { isPublishedTmsEventStream } from "./base";
import { dutyStatusChangeKinds } from "./subjects/dutyStatusChange/facts";
import { formSubmissionKinds } from "./subjects/formSubmission/facts";
import { hosClockViolationKinds } from "./subjects/hosClockViolation/facts";
import { routeAssignmentEventKinds } from "./subjects/routeAssignmentEvent/facts";
import { stopEventKinds } from "./subjects/stopEvent/facts";
import { vehicleDriverAssignmentTransitionKinds } from "./subjects/vehicleDriverAssignmentTransition/facts";
import type { TmsEvent, TmsEventCategory, TmsEventKind } from "./taxonomy";
import { TMS_EVENT_CATEGORIES, tmsEventKindCategory } from "./taxonomy";

/**
 * What each subject states and where it is collated: its kind list, and the stream its events belong to.
 *
 * Each kind list is declared beside its subject in `subjects/<subject>/facts.ts`, and this is the one place they meet
 * — and the one place a subject's stream is declared, which the registry's definitions read rather than restate. A
 * reader that needs a fact per subject rather than per kind keys a table on {@link TmsEventSubject} and reaches it
 * through {@link tmsEventSubjectOf}. Keyed by the subject's directory name rather than the kernel `Subject` value,
 * which this package does not know and which most of these subjects do not have yet.
 */
export const tmsEventSubjects = {
  dutyStatusChange: { kinds: dutyStatusChangeKinds, stream: "driver_event" },
  formSubmission: { kinds: formSubmissionKinds, stream: "driver_event" },
  hosClockViolation: { kinds: hosClockViolationKinds, stream: "driver_event" },
  routeAssignmentEvent: { kinds: routeAssignmentEventKinds, stream: "route_event" },
  stopEvent: { kinds: stopEventKinds, stream: "route_event" },
  vehicleDriverAssignmentTransition: { kinds: vehicleDriverAssignmentTransitionKinds, stream: "assignment" },
} as const satisfies Record<string, { kinds: readonly TmsEventKind[]; stream: TmsEventStream }>;

export type TmsEventSubject = keyof typeof tmsEventSubjects;

/**
 * The kinds one subject states, as a union.
 */
export type TmsEventKindOfSubject<TSubject extends TmsEventSubject> =
  (typeof tmsEventSubjects)[TSubject]["kinds"][number];

/**
 * The stream one subject is collated into.
 */
export type TmsEventStreamOfSubject<TSubject extends TmsEventSubject> = (typeof tmsEventSubjects)[TSubject]["stream"];

/**
 * The subject that states one kind; `never` for a kind no list claims, which the unit test refuses at compile time.
 */
export type TmsEventSubjectOfKind<TKind extends TmsEventKind> = {
  [TSubject in TmsEventSubject]: TKind extends TmsEventKindOfSubject<TSubject> ? TSubject : never;
}[TmsEventSubject];

/**
 * The subjects collated into one stream, or into any of several.
 */
export type TmsEventSubjectInStream<TStream extends TmsEventStream> = {
  [TSubject in TmsEventSubject]: TmsEventStreamOfSubject<TSubject> extends TStream ? TSubject : never;
}[TmsEventSubject];

/**
 * The kinds a page of one stream can carry: those of the subjects collated into it.
 */
export type TmsEventKindInStream<TStream extends TmsEventStream> = TmsEventKindOfSubject<
  TmsEventSubjectInStream<TStream>
>;

/**
 * One event as a page of the given stream carries it: the DTO union narrowed to that stream's kinds.
 *
 * What lets a read typed by the stream it was asked for hand a published stream's page to the wire without a cast:
 * a published stream's subjects are published by definition, so their kinds are the wire's.
 */
export type TmsEventInStream<TStream extends TmsEventStream> = Extract<
  TmsEvent,
  { kind: TmsEventKindInStream<TStream> }
>;

/**
 * The subjects whose stream leaves the backend, and so whose kinds a client can ever be sent.
 */
export type PublishedTmsEventSubject = {
  [TSubject in TmsEventSubject]: TmsEventStreamOfSubject<TSubject> extends PublishedTmsEventStream ? TSubject : never;
}[TmsEventSubject];

/**
 * Every kind a client can be sent: the kinds of the published subjects.
 */
export type PublishedTmsEventKind = TmsEventKindOfSubject<PublishedTmsEventSubject>;

const subjectByKind: Record<string, TmsEventSubject> = Object.fromEntries(
  Object.entries(tmsEventSubjects).flatMap(([subject, { kinds }]) =>
    kinds.map((kind): [string, TmsEventSubject] => [kind, subject as TmsEventSubject]),
  ),
);

/**
 * The subject that states one kind.
 *
 * @param kind - A catalog kind; the type system refuses any other, so a throw here means a cast reached it.
 * @throws if no subject's list names the kind.
 */
export function tmsEventSubjectOf<TKind extends TmsEventKind>(kind: TKind): TmsEventSubjectOfKind<TKind> {
  const subject = subjectByKind[kind];
  if (subject === undefined) {
    throw new Error(`No TMS event subject states kind "${kind}"`);
  }

  return subject as TmsEventSubjectOfKind<TKind>;
}

/**
 * The stream one kind's events are collated into: its subject's.
 *
 * @param kind - A catalog kind.
 */
export function tmsEventStreamOf<TKind extends TmsEventKind>(
  kind: TKind,
): TmsEventStreamOfSubject<TmsEventSubjectOfKind<TKind>> {
  return tmsEventSubjects[tmsEventSubjectOf(kind)].stream as TmsEventStreamOfSubject<TmsEventSubjectOfKind<TKind>>;
}

/**
 * Whether a kind can reach a client: its subject's stream is published.
 */
export function isPublishedTmsEventKind(kind: TmsEventKind): kind is PublishedTmsEventKind {
  return isPublishedTmsEventStream(tmsEventStreamOf(kind));
}

/**
 * A subject as its tables and the wire name it: `stopEvent` → `stop_event`.
 */
export function tmsEventSubjectTableName(subject: TmsEventSubject): string {
  return subject.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * The catalog's categories as the kinds of some subjects fill them, in catalog order.
 *
 * Each category with the kinds of the named subjects, and only categories left with one. The reserved uncategorized
 * key is never among them.
 *
 * @param subjects - The subjects whose kinds to keep, by table name (`stop_event`); any other name is ignored.
 */
export function tmsEventCategoriesStatedBy(
  subjects: Iterable<string>,
): { category: TmsEventCategory; kinds: TmsEventKind[] }[] {
  const stated = new Set(subjects);
  return TMS_EVENT_CATEGORIES.map((category) => ({
    category,
    kinds: (Object.keys(tmsEventKindCategory) as TmsEventKind[]).filter(
      (kind) =>
        tmsEventKindCategory[kind] === category && stated.has(tmsEventSubjectTableName(tmsEventSubjectOf(kind))),
    ),
  })).filter(({ kinds }) => kinds.length > 0);
}
